import React from "react";
import { motion } from "framer-motion";
import { Icon } from "@/lib/icons";

export default function Services({ data }) {
  const items = (data?.items || []).filter((s) => s.enabled !== false);

  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          {data?.eyebrow && (
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#B8941F] font-semibold mb-4">
              {data.eyebrow}
            </p>
          )}
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A0A0A] leading-tight">
            {data?.title?.split(" ").slice(0, -1).join(" ")}{" "}
            <span className="text-gradient-gold">{data?.title?.split(" ").slice(-1)[0]}</span>
          </h2>
          {data?.subtitle && (
            <p className="mt-4 text-gray-600 text-base sm:text-lg max-w-xl mx-auto">{data.subtitle}</p>
          )}
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {items.map((s, i) => (
            <motion.div
              key={s.id || s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group relative bg-white rounded-2xl border border-[#EFEAD8] overflow-hidden flex flex-col text-center transition-all duration-500 hover:border-[#D4AF37]/60 hover:-translate-y-1 hover:shadow-[0_22px_40px_-18px_rgba(212,175,55,0.4)]"
              data-testid={`service-card-${i}`}
            >
              {/* Image with gradient fade */}
              <div className="relative h-48 sm:h-56 w-full overflow-hidden">
                {s.image ? (
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A]" />
                )}
                {/* Bottom fade to white so the icon circle blends */}
                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-white pointer-events-none" />
              </div>

              {/* Floating circular icon overlapping image and text */}
              <div className="relative -mt-11 mb-2 flex justify-center">
                <div className="w-20 h-20 rounded-full bg-white border border-[#D4AF37]/30 flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(212,175,55,0.5)] group-hover:bg-[#FBF8F1] transition-colors">
                  <Icon name={s.icon} className="w-8 h-8 text-[#B8941F]" />
                </div>
              </div>

              <div className="px-5 pb-7 flex-1 flex flex-col">
                <h3 className="font-display text-xl font-bold text-[#0A0A0A] mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>

              {/* Subtle hover line */}
              <div className="h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
